import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showSpriteMessage, hideSpriteMessage } from "../../store/sceneSlice";
import textToSpeechManager from "../../utils/TextToSpeechManager";
import { NUMBER_DETECT_CROP } from "../../constants/numberDetection";

const SCAN_COOLDOWN_MS = 5000;
const QUEUE_LIMIT = 5;
const CAMERA_POLL_MS = 400;
const OCR_INTERVAL_MS = 800;
const READY_STATE_HAVE_DATA = 2;
const SPEECH_IDLE_POLL_MS = 200;
const SPEECH_IDLE_MAX_WAIT_MS = 6000;
const OCR_LANG = "eng";
const DIGIT_WHITELIST = "0123456789";
const TARGET_CANVAS_WIDTH = 640;
const OCR_CONFIDENCE_MIN = 70;
const STABLE_MATCHES_REQUIRED = 2;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const extractDigits = (text) => {
  if (!text) return "";
  const parts = String(text).match(/\d+/g);
  if (!parts || !parts.length) return "";
  return parts.join("");
};

const pickBestDigits = (result) => {
  const words = Array.isArray(result?.data?.words) ? result.data.words : [];
  let best = { text: "", confidence: -1 };
  words.forEach((word) => {
    const digits = extractDigits(word?.text || "");
    if (!digits) return;
    const confidence = Number.isFinite(word?.confidence) ? word.confidence : -1;
    if (confidence > best.confidence || (confidence === best.confidence && digits.length > best.text.length)) {
      best = { text: digits, confidence };
    }
  });
  if (best.text) return best;
  const fallbackText = extractDigits(result?.data?.text || "");
  const fallbackConfidence = Number.isFinite(result?.data?.confidence) ? result.data.confidence : -1;
  return { text: fallbackText, confidence: fallbackConfidence };
};

const buildFrameCanvas = (videoEl, canvas) => {
  const videoWidth = videoEl.videoWidth || videoEl.clientWidth || 0;
  const videoHeight = videoEl.videoHeight || videoEl.clientHeight || 0;
  if (!videoWidth || !videoHeight) return null;

  const cropWidth = Math.max(1, Math.round(videoWidth * NUMBER_DETECT_CROP.widthRatio));
  const cropHeight = Math.max(1, Math.round(videoHeight * NUMBER_DETECT_CROP.heightRatio));
  const cropX = Math.max(0, Math.round((videoWidth - cropWidth) / 2));
  const cropY = Math.max(0, Math.round((videoHeight - cropHeight) / 2));

  const scale = Math.min(1, TARGET_CANVAS_WIDTH / cropWidth);
  const targetWidth = Math.max(1, Math.round(cropWidth * scale));
  const targetHeight = Math.max(1, Math.round(cropHeight * scale));

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(videoEl, cropX, cropY, cropWidth, cropHeight, 0, 0, targetWidth, targetHeight);
  return canvas;
};

const NumberDetectionController = ({ selectedActorId }) => {
  const dispatch = useDispatch();
  const { scenes, currentSceneIndex, numberDetectEnabled } = useSelector((s) => s.scene);

  const actorInfo = useMemo(() => {
    const scene = scenes?.[currentSceneIndex];
    const actors = scene?.actors || [];
    if (selectedActorId) {
      const target = actors.find((a) => a.id === selectedActorId) || actors[0] || null;
      return {
        id: target?.id || null,
        lang: target?.speechSettings?.lang || "en-US",
      };
    }
    const fallback = actors[0] || null;
    return {
      id: fallback?.id || null,
      lang: fallback?.speechSettings?.lang || "en-US",
    };
  }, [scenes, currentSceneIndex, selectedActorId]);

  const actorId = actorInfo?.id || null;
  const actorLang = actorInfo?.lang || "en-US";

  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const lastSeenRef = useRef(new Map());
  const activeRef = useRef(false);
  const workerRef = useRef(null);
  const inFlightRef = useRef(false);
  const canvasRef = useRef(null);
  const terminateRequestedRef = useRef(false);
  const candidateRef = useRef({ value: "", count: 0 });

  const waitForSpeechIdle = async () => {
    const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (!synth) return;
    if (!synth.speaking && !synth.pending) return;
    const start = Date.now();
    while ((synth.speaking || synth.pending) && Date.now() - start < SPEECH_IDLE_MAX_WAIT_MS) {
      await delay(SPEECH_IDLE_POLL_MS);
    }
  };

  const pumpQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (activeRef.current && queueRef.current.length) {
        const item = queueRef.current.shift();
        if (!item?.text) continue;
        try {
          await waitForSpeechIdle();
          const spoke = await textToSpeechManager.speak(item.text, item.lang);
          if (!spoke) {
            await delay(1200);
          }
        } catch (err) {
          console.warn("Number speech failed:", err);
        }
      }
    } finally {
      processingRef.current = false;
    }
  };

  const enqueueSpeech = (text) => {
    const queue = queueRef.current;
    while (queue.length >= QUEUE_LIMIT) {
      queue.shift();
    }
    queue.push({ text, lang: actorLang });
    pumpQueue();
  };

  const handleDetection = (rawText) => {
    if (!activeRef.current) return;
    const text = String(rawText || "").trim();
    if (!text) return;
    const now = Date.now();
    const lastAt = lastSeenRef.current.get(text) || 0;
    if (now - lastAt < SCAN_COOLDOWN_MS) return;
    lastSeenRef.current.set(text, now);

    if (actorId) {
      dispatch(showSpriteMessage({ actorId, message: text }));
    }
    enqueueSpeech(text);
  };

  const ensureWorker = async () => {
    if (workerRef.current) return workerRef.current;
    const tesseract = await import("tesseract.js");
    const worker = await tesseract.createWorker();
    await worker.loadLanguage(OCR_LANG);
    await worker.initialize(OCR_LANG);
    await worker.setParameters({
      tessedit_char_whitelist: DIGIT_WHITELIST,
      tessedit_pageseg_mode: "7",
    });
    workerRef.current = worker;
    return worker;
  };

  const shutdownWorker = async () => {
    const worker = workerRef.current;
    if (!worker) return;
    workerRef.current = null;
    terminateRequestedRef.current = false;
    try {
      await worker.terminate();
    } catch (err) {
      console.warn("Number OCR worker terminate failed:", err);
    }
  };

  const requestWorkerShutdown = () => {
    terminateRequestedRef.current = true;
    if (!inFlightRef.current) {
      shutdownWorker();
    }
  };

  useEffect(() => {
    activeRef.current = numberDetectEnabled;
    if (!numberDetectEnabled) {
      queueRef.current = [];
      processingRef.current = false;
    }
    return () => {
      activeRef.current = false;
    };
  }, [numberDetectEnabled]);

  useEffect(() => {
    if (!numberDetectEnabled) {
      if (actorId) {
        dispatch(hideSpriteMessage({ actorId }));
      }
      return undefined;
    }

    let cancelled = false;
    if (!canvasRef.current && typeof document !== "undefined") {
      canvasRef.current = document.createElement("canvas");
    }

    const tick = async () => {
      if (cancelled) return;
      if (!activeRef.current) {
        setTimeout(tick, OCR_INTERVAL_MS);
        return;
      }
      if (inFlightRef.current) {
        setTimeout(tick, OCR_INTERVAL_MS);
        return;
      }

      const data = window.humanDetectionData || {};
      const videoEl = data.videoEl;
      const cameraActive = data.cameraActive;
      if (!cameraActive || !videoEl || videoEl.readyState < READY_STATE_HAVE_DATA) {
        setTimeout(tick, CAMERA_POLL_MS);
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) {
        setTimeout(tick, OCR_INTERVAL_MS);
        return;
      }

      const frame = buildFrameCanvas(videoEl, canvas);
      if (!frame) {
        setTimeout(tick, OCR_INTERVAL_MS);
        return;
      }

      inFlightRef.current = true;
      try {
        const worker = await ensureWorker();
        const result = await worker.recognize(frame);
        const candidate = pickBestDigits(result);
        const digits = candidate?.text || "";
        const confidence = Number.isFinite(candidate?.confidence) ? candidate.confidence : -1;
        if (digits && confidence >= OCR_CONFIDENCE_MIN) {
          if (candidateRef.current.value === digits) {
            candidateRef.current.count += 1;
          } else {
            candidateRef.current.value = digits;
            candidateRef.current.count = 1;
          }
          if (candidateRef.current.count >= STABLE_MATCHES_REQUIRED) {
            candidateRef.current.count = 0;
            handleDetection(digits);
          }
        } else {
          candidateRef.current.value = "";
          candidateRef.current.count = 0;
        }
      } catch (err) {
        console.warn("Number OCR failed:", err);
      } finally {
        inFlightRef.current = false;
        if (terminateRequestedRef.current && !activeRef.current) {
          await shutdownWorker();
        }
        setTimeout(tick, OCR_INTERVAL_MS);
      }
    };

    setTimeout(tick, OCR_INTERVAL_MS);

    return () => {
      cancelled = true;
      requestWorkerShutdown();
    };
  }, [numberDetectEnabled, actorId, actorLang, dispatch]);

  return null;
};

export default NumberDetectionController;
